import { ArrowDownIcon, ArrowRightIcon, ArrowTopRightIcon, GridIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import { Button, Callout, Card, Select, Spinner } from '@radix-ui/themes';
import axios from 'axios';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE, scorePath, useTeams } from '../api';
import { gridLegend } from './gridLegend';
import SearchableTeamSelect from './SearchableTeamSelect';
import './Home.css';

const ScoreGrid = lazy(() => import('./ScoreGrid'));
type ResultFilter = 'both' | 'wins' | 'losses';

interface Stats {
  total_games: number;
  total_unique_scores: number;
}

function LoadingGrid() {
  return <div className="grid-state" role="status"><Spinner /> Loading the score grid…</div>;
}

function FieldGraphic() {
  return (
    <div className="field-graphic" aria-hidden="true">
      <svg viewBox="0 0 520 260" preserveAspectRatio="xMidYMid slice" className="field-lines">
        <defs>
          <pattern id="yard-lines" width="52" height="260" patternUnits="userSpaceOnUse">
            <path d="M1 0v260M26 55v12m0 126v12M10 114v6m12-6v6m12-6v6m12-6v6M10 140v6m12-6v6m12-6v6m12-6v6" fill="none" stroke="#9994d5" strokeOpacity=".27" />
          </pattern>
          <marker id="play-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="m2 1 6 4-6 4" fill="none" stroke="#f9df5b" strokeWidth="1.5" />
          </marker>
        </defs>
        <path fill="url(#yard-lines)" d="M0 0h520v260H0z" />
        <path d="M0 32h520M0 228h520" stroke="#9994d5" strokeOpacity=".4" />
        <path d="M28 206h58q18 0 18-18v-50l46-43M322 209h54q14 0 14-14v-35l82-78" fill="none" stroke="#f9df5b" strokeWidth="1.5" strokeDasharray="5 6" markerEnd="url(#play-arrow)" />
        <circle cx="28" cy="206" r="4" fill="#f9df5b" />
        <circle cx="322" cy="209" r="4" fill="#f9df5b" />
      </svg>
      <div className="field-label"><span className="status-dot" /> SCOREBOARD</div>
      <div className="field-score">
        <span>31</span><span className="field-score-sep">:</span><span>28</span>
      </div>
      <div className="field-caption"><span>FINAL SCORE</span><span>01 / CFB</span></div>
    </div>
  );
}

export default function Home() {
  const { teams, unavailable } = useTeams();
  const [teamId, setTeamId] = useState<number | null>(null);
  const [filter, setFilter] = useState<ResultFilter>('both');
  const [stats, setStats] = useState<Stats | null>(null);
  const [grid, setGrid] = useState<number[][] | null>(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    axios.get<Stats>(`${API_BASE}/api/stats/`, { signal: controller.signal })
      .then(response => setStats(response.data))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setGrid(null);
    setFailed(false);
    const params = teamId === null ? undefined : { team_id: teamId, filter };
    axios.get<{ grid: number[][] }>(`${API_BASE}/api/grid/`, { params, signal: controller.signal })
      .then(response => setGrid(response.data.grid))
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, [teamId, filter, retry]);

  const teamName = teams.find(team => team.id === teamId)?.name;
  const teamScoreCount = grid?.flat().filter(value => value > 0 && value < 1).length;

  return (
    <>
      <section className="archive-hero" aria-labelledby="archive-title">
        <div>
          <p className="eyebrow"><span className="status-dot" /> College football score archive</p>
          <h1 className="display-title" id="archive-title">Every score.<br /><em>Ever.</em></h1>
          <p className="hero-copy">Explore final scores from across college football history. Find the familiar, the forgotten, and the ones we’re still waiting for.</p>
          <div className="hero-actions">
            <Button asChild size="3" className="action-primary"><a href="#score-grid">Explore the grid <ArrowDownIcon /></a></Button>
            <Button asChild size="3" variant="outline" color="gray" className="action-secondary"><Link to="/score_lookup">Look up a score <ArrowRightIcon /></Link></Button>
          </div>
        </div>
        <div className="hero-aside">
          <FieldGraphic />
          <dl className="archive-stats">
            <div><dt>Games indexed</dt><dd>{stats?.total_games.toLocaleString() ?? '—'}</dd></div>
            <div><dt>Unique scores</dt><dd>{stats?.total_unique_scores.toLocaleString() ?? '—'}</dd></div>
            <div><dt>First season</dt><dd>1869</dd></div>
          </dl>
        </div>
      </section>

      <section id="score-grid" aria-labelledby="grid-title" className="grid-section">
        <div className="grid-heading">
          <div>
            <p className="eyebrow"><GridIcon /> The all-time map</p>
            <h2 className="section-title" id="grid-title">Score grid</h2>
            <p className="section-description">Select a square to see the games behind that score.</p>
          </div>
          <div className="grid-filters">
            <SearchableTeamSelect
              label="Filter by team"
              teams={teams}
              selectedTeam={teamId}
              unavailable={unavailable}
              onTeamChange={id => {
                setTeamId(id);
                if (id === null) setFilter('both');
              }}
            />
            {teamId !== null && (
              <div className="result-filter">
                <label className="field-label" htmlFor="result-filter">Result</label>
                <Select.Root size="3" value={filter} onValueChange={value => setFilter(value as ResultFilter)}>
                  <Select.Trigger id="result-filter" color="gray" />
                  <Select.Content position="popper" color="gray">
                    <Select.Item value="both">Wins &amp; losses</Select.Item>
                    <Select.Item value="wins">Wins only</Select.Item>
                    <Select.Item value="losses">Losses only</Select.Item>
                  </Select.Content>
                </Select.Root>
              </div>
            )}
          </div>
        </div>
        <Card className="grid-card">
          <div className="grid-meta">
            <div className="grid-scope"><span className="status-dot" /> {teamName ?? 'All teams'}{teamScoreCount !== undefined && teamId !== null && <span> · {teamScoreCount.toLocaleString()} unique scores</span>}</div>
            <div className="grid-legend" aria-label="Grid legend">
              <span className="legend-label">Games per score</span>
              {gridLegend.map(([color, label]) => (
                <span key={label}><i style={{ backgroundColor: color }} aria-hidden="true" />{label}</span>
              ))}
            </div>
          </div>
          {failed ? (
            <div className="grid-state">
              <Callout.Root color="red" role="alert">
                <Callout.Icon><InfoCircledIcon /></Callout.Icon>
                <Callout.Text>The score grid couldn’t load.</Callout.Text>
              </Callout.Root>
              <Button variant="soft" onClick={() => setRetry(retry + 1)}>Try again</Button>
            </div>
          ) : grid ? (
            <Suspense fallback={<LoadingGrid />}><ScoreGrid grid={grid} teamId={teamId} /></Suspense>
          ) : <LoadingGrid />}
          <div className="grid-footnote">
            <span className="swipe-note">Scroll horizontally to see more scores. </span>
            The grid shows winning scores up to 165. <Link to="/score_lookup">Search the full archive <ArrowTopRightIcon /></Link>
          </div>
        </Card>
      </section>

      <section className="archive-notes" aria-label="About the archive">
        <Card className="note-card">
          <p className="eyebrow">01 / The idea</p>
          <h3>What’s a Scorigami?</h3>
          <p>It’s a final score that has never happened before. <a href="https://www.youtube.com/watch?v=9l5C8cGMueY" target="_blank" rel="noreferrer">Jon Bois</a> coined the term. This grid tracks it for college football.</p>
        </Card>
        <Card className="note-card">
          <p className="eyebrow">02 / The diagonal</p>
          <h3>Why are there ties?</h3>
          <p>College football once allowed ties. The grid keeps historical ties and marks unused tie scores as impossible.</p>
        </Card>
        <Card className="note-card">
          <p className="eyebrow">03 / The outliers</p>
          <h3>Way off the grid</h3>
          <p>Some scores are too big for the chart. You can still look up <Link to={scorePath(167, 0)}>167–0</Link>, <Link to={scorePath(205, 0)}>205–0</Link>, and even <Link to={scorePath(222, 0)}>222–0</Link>.</p>
        </Card>
      </section>
    </>
  );
}
